import {
  Document,
  Font,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import { VerticalReport } from "./types";

Font.register({
  family: "Roboto",
  fonts: [
    { src: "/fonts/Roboto-Regular.ttf", fontWeight: "normal" },
    { src: "/fonts/Roboto-Bold.ttf", fontWeight: "bold" },
  ],
});

const usd = (n: number) =>
  `$${(Math.abs(n) / 100)
    .toFixed(2)
    .replace(/\d(?=(\d{3})+\.)/g, "$&,")}`;

const Line = ({
  label,
  value,
  bold,
  large,
  color,
}: {
  label: string;
  value: string;
  bold?: boolean;
  large?: boolean;
  color?: string;
}) => (
  <View style={s.line}>
    <Text style={[s.lineLabel, bold ? s.bold : {}, large ? s.large : {}]}>{label}</Text>
    <Text style={[s.lineValue, bold ? s.bold : {}, large ? s.large : {}, color ? { color } : {}]}>
      {value}
    </Text>
  </View>
);

interface Props {
  report: VerticalReport;
}

export const VerticalSalesPDF: React.FC<Props> = ({ report }) => {
  // Roll up to per-category totals — the PDF shows a summary, not the line items.
  const categoryMap = new Map<string, { category: string; units: number; gross: number; tax: number; discounts: number; net: number }>();
  for (const p of report.products) {
    const existing = categoryMap.get(p.category);
    if (existing) {
      existing.units += p.unitsSold;
      existing.gross += p.grossSales;
      existing.tax += p.tax;
      existing.discounts += p.discounts;
      existing.net += p.netSales;
    } else {
      categoryMap.set(p.category, {
        category: p.category,
        units: p.unitsSold,
        gross: p.grossSales,
        tax: p.tax,
        discounts: p.discounts,
        net: p.netSales,
      });
    }
  }
  const categories = Array.from(categoryMap.values()).sort((a, b) => a.category.localeCompare(b.category));

  const productTotals = categories.reduce(
    (acc, c) => {
      acc.units += c.units;
      acc.gross += c.gross;
      acc.tax += c.tax;
      acc.discounts += c.discounts;
      acc.net += c.net;
      return acc;
    },
    { units: 0, gross: 0, tax: 0, discounts: 0, net: 0 }
  );

  return (
    <Document>
      <Page size="LETTER" style={s.page}>
        <View style={s.header} fixed>
          <Image style={s.logo} src="/static/Ronin_RGB_POS.png" />
          <View style={s.headerRight}>
            <Text style={s.title}>Vertical Sales Report</Text>
            <Text style={s.subtitle}>{report.eventName}</Text>
          </View>
        </View>

        <View style={s.vendorBanner} fixed>
          <Text style={s.vendorBannerText}>{report.vendorName}</Text>
          <Text style={s.vendorBannerSub}>
            {report.transactionCount.toLocaleString()} transaction{report.transactionCount === 1 ? "" : "s"}
          </Text>
        </View>

        <View style={s.section} wrap={false}>
          <Text style={s.sectionTitle}>Summary</Text>
          <Line label="Gross Sales" value={usd(report.grossSales)} bold />
          <Line label="Less Tax" value={`(${usd(report.tax)})`} />
          <Line label="Less Discounts" value={`(${usd(report.discounts)})`} />
          <Line label="Less Refunds" value={`(${usd(report.refunds)})`} />
          <Line label="Tips" value={usd(report.tips)} />
          {(report.revenueShareCut > 0 || report.flatFee > 0) && (
            <>
              <Line label="Less Organizer Share" value={`(${usd(report.revenueShareCut + report.flatFee)})`} />
              {report.revenueShareCut > 0 && (
                <Line label="    Revenue share" value={usd(report.revenueShareCut)} />
              )}
              {report.flatFee > 0 && (
                <Line label="    Flat fee" value={usd(report.flatFee)} />
              )}
            </>
          )}
          <View style={s.divider} />
          <Line label="Net Sales (Vendor)" value={usd(report.netSales)} bold large color="#B71C1C" />
        </View>

        <View style={s.section} wrap={false}>
          <Text style={s.sectionTitle}>Payment Breakdown</Text>
          {report.payments.map((p) => (
            <Line key={p.label} label={`${p.label} (${(p.share * 100).toFixed(1)}%)`} value={usd(p.amount)} />
          ))}
          <View style={s.divider} />
          <Line
            label="Total"
            value={usd(report.payments.reduce((acc, r) => acc + r.amount, 0))}
            bold
          />
        </View>

        {report.cardBrands.length > 0 && (
          <View style={s.section} wrap={false}>
            <Text style={s.sectionTitle}>Card Brands</Text>
            <View style={[s.tableRow, s.tableHeader]}>
              <Text style={[s.tableCell, s.colBrand, s.bold]}>Brand</Text>
              <Text style={[s.tableCell, s.colNum, s.bold]}>Transactions</Text>
              <Text style={[s.tableCell, s.colNum, s.bold]}>Amount</Text>
            </View>
            {report.cardBrands.map((b) => (
              <View key={b.brand} style={s.tableRow} wrap={false}>
                <Text style={[s.tableCell, s.colBrand]}>{b.brand}</Text>
                <Text style={[s.tableCell, s.colNum]}>{b.count.toLocaleString()}</Text>
                <Text style={[s.tableCell, s.colNum]}>{usd(b.amount)}</Text>
              </View>
            ))}
            <View style={[s.tableRow, s.tableTotalRow]} wrap={false}>
              <Text style={[s.tableCell, s.colBrand, s.bold]}>Total</Text>
              <Text style={[s.tableCell, s.colNum, s.bold]}>
                {report.cardBrands.reduce((acc, b) => acc + b.count, 0).toLocaleString()}
              </Text>
              <Text style={[s.tableCell, s.colNum, s.bold]}>
                {usd(report.cardBrands.reduce((acc, b) => acc + b.amount, 0))}
              </Text>
            </View>
          </View>
        )}

        <View style={s.section}>
          <Text style={s.sectionTitle}>Product Mix Summary</Text>
          <View style={[s.tableRow, s.tableHeader]}>
            <Text style={[s.tableCell, s.colCategory, s.bold]}>Category</Text>
            <Text style={[s.tableCell, s.colNum, s.bold]}>Units</Text>
            <Text style={[s.tableCell, s.colNum, s.bold]}>Gross</Text>
            <Text style={[s.tableCell, s.colNum, s.bold]}>Tax</Text>
            <Text style={[s.tableCell, s.colNum, s.bold]}>Disc</Text>
            <Text style={[s.tableCell, s.colNum, s.bold]}>Net</Text>
          </View>
          {categories.map((c, idx) => (
            <View key={idx} style={s.tableRow} wrap={false}>
              <Text style={[s.tableCell, s.colCategory]}>{c.category}</Text>
              <Text style={[s.tableCell, s.colNum]}>{c.units.toLocaleString()}</Text>
              <Text style={[s.tableCell, s.colNum]}>{usd(c.gross)}</Text>
              <Text style={[s.tableCell, s.colNum]}>{usd(c.tax)}</Text>
              <Text style={[s.tableCell, s.colNum]}>{usd(c.discounts)}</Text>
              <Text style={[s.tableCell, s.colNum]}>{usd(c.net)}</Text>
            </View>
          ))}
          <View style={[s.tableRow, s.tableTotalRow]} wrap={false}>
            <Text style={[s.tableCell, s.colCategory, s.bold]}>Total</Text>
            <Text style={[s.tableCell, s.colNum, s.bold]}>{productTotals.units.toLocaleString()}</Text>
            <Text style={[s.tableCell, s.colNum, s.bold]}>{usd(productTotals.gross)}</Text>
            <Text style={[s.tableCell, s.colNum, s.bold]}>{usd(productTotals.tax)}</Text>
            <Text style={[s.tableCell, s.colNum, s.bold]}>{usd(productTotals.discounts)}</Text>
            <Text style={[s.tableCell, s.colNum, s.bold]}>{usd(productTotals.net)}</Text>
          </View>
        </View>

        <Text
          style={s.footer}
          render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
          fixed
        />
      </Page>
    </Document>
  );
};

const s = StyleSheet.create({
  page: { padding: 28, backgroundColor: "#FFFFFF", fontFamily: "Roboto", fontSize: 9 },
  header: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
  headerRight: { textAlign: "right" },
  logo: { width: 80, height: "auto" },
  title: { fontSize: 18, fontWeight: "bold", color: "#B71C1C", marginBottom: 3 },
  subtitle: { fontSize: 12, color: "#333" },
  vendorBanner: { backgroundColor: "#B71C1C", padding: 8, borderRadius: 3, marginBottom: 12, flexDirection: "row", justifyContent: "space-between", alignItems: "center" },
  vendorBannerText: { fontSize: 14, fontWeight: "bold", color: "#FFFFFF" },
  vendorBannerSub: { fontSize: 10, color: "#FFCDD2" },
  section: { marginBottom: 10, borderWidth: 1, borderColor: "#D0D0D0", borderRadius: 2, overflow: "hidden" },
  sectionTitle: { fontSize: 10, fontWeight: "bold", color: "#FFFFFF", backgroundColor: "#B71C1C", paddingVertical: 4, paddingHorizontal: 6 },
  line: { flexDirection: "row", justifyContent: "space-between", paddingVertical: 3, paddingHorizontal: 8, borderBottomWidth: 0.5, borderBottomColor: "#E8E8E8" },
  lineLabel: { fontSize: 9, color: "#333" },
  lineValue: { fontSize: 9, color: "#333", textAlign: "right" },
  bold: { fontWeight: "bold" },
  large: { fontSize: 13 },
  divider: { borderBottomWidth: 1.5, borderBottomColor: "#999", marginVertical: 2 },
  tableRow: { flexDirection: "row", borderBottomWidth: 0.5, borderBottomColor: "#E8E8E8" },
  tableHeader: { backgroundColor: "#F5F5F5" },
  tableTotalRow: { backgroundColor: "#FAFAFA", borderTopWidth: 1, borderTopColor: "#999" },
  tableCell: { fontSize: 8, paddingVertical: 3, paddingHorizontal: 6 },
  colCategory: { width: "30%" },
  colBrand: { width: "72%" },
  colNum: { width: "14%", textAlign: "right" },
  footer: { position: "absolute", bottom: 16, left: 0, right: 0, textAlign: "center", fontSize: 7, color: "#999" },
});
